import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // Aguardar o morph completar e então inicializar o slideshow
    // O slideshow precisa aguardar o connectedCallback ser executado
    this.#initializeMediaGallery(modalContent);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Inicializa a galeria de mídia após o morph
   * @param {HTMLElement} modalContent - O conteúdo do modal
   */
  #initializeMediaGallery(modalContent) {
    // Primeiro, garantir que o primeiro slide esteja visível imediatamente
    // Isso evita o problema de content-visibility: hidden
    const mediaGallery = modalContent.querySelector('.product-information__media media-gallery');
    if (mediaGallery) {
      // Se o media-gallery tem classes de animação, forçar visibilidade imediata
      // Isso é necessário porque animações começam com opacity: 0
      if (mediaGallery.classList.contains('media-gallery--animated')) {
        mediaGallery.classList.add('is-visible');
      }

      const firstSlide = mediaGallery.querySelector('slideshow-slide:first-child');
      if (firstSlide) {
        // Forçar visibilidade imediata do primeiro slide
        firstSlide.setAttribute('aria-hidden', 'false');
        firstSlide.removeAttribute('hidden');
        firstSlide.style.contentVisibility = 'visible';
      }
    }

    // Aguardar múltiplos frames para garantir que o DOM e os componentes estejam prontos
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (!mediaGallery) return;

          // Garantir novamente que o media-gallery está visível se tiver animação
          if (mediaGallery.classList.contains('media-gallery--animated')) {
            mediaGallery.classList.add('is-visible');
          }

          const slideshow = mediaGallery.querySelector('slideshow-component');
          if (slideshow) {
            // Aguardar o slideshow estar totalmente conectado e inicializado
            // O connectedCallback pode levar um pouco de tempo
            const checkSlideshow = () => {
              if (slideshow.isConnected) {
                // Slideshow está conectado, inicializar
                this.#initializeSlideshow(slideshow);
              } else {
                // Ainda não está pronto, tentar novamente
                setTimeout(checkSlideshow, 50);
              }
            };
            
            // Começar a verificação após um pequeno delay
            setTimeout(checkSlideshow, 100);
          } else {
            // Se não encontrar slideshow-component, garantir que o primeiro slide apareça
            const firstSlide = mediaGallery.querySelector('slideshow-slide:first-child');
            if (firstSlide) {
              firstSlide.setAttribute('aria-hidden', 'false');
              firstSlide.removeAttribute('hidden');
              firstSlide.style.contentVisibility = 'visible';
            }
          }
        }, 100);
      });
    });
  }

  /**
   * Inicializa o slideshow para mostrar a primeira imagem
   * @param {HTMLElement} slideshow - O elemento slideshow-component
   */
  #initializeSlideshow(slideshow) {
    if (!slideshow || !slideshow.isConnected) return;

    // Primeiro, garantir que todos os slides tenham aria-hidden correto
    const allSlides = slideshow.querySelectorAll('slideshow-slide');
    allSlides.forEach((slide, index) => {
      if (index === 0) {
        // Primeiro slide deve estar visível
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('hidden');
        slide.style.contentVisibility = 'visible';
      } else {
        // Outros slides devem estar escondidos
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    // Verificar se o slideshow tem slides
    const slides = slideshow.refs?.slides || allSlides;
    if (!slides || slides.length === 0) return;

    // Tentar selecionar o primeiro slide usando o método do slideshow
    if (typeof slideshow.select === 'function') {
      try {
        // Usar o mesmo padrão que o slideshow usa na inicialização
        slideshow.select(0, undefined, { animate: false });
      } catch (e) {
        console.warn('Failed to initialize slideshow via select method:', e);
        // Fallback manual
        this.#showFirstSlide(slideshow);
      }
    } else {
      // Fallback manual
      this.#showFirstSlide(slideshow);
    }
  }

  /**
   * Mostra o primeiro slide manualmente
   * @param {HTMLElement} slideshow - O elemento slideshow-component
   */
  #showFirstSlide(slideshow) {
    const firstSlide = slideshow.querySelector('slideshow-slide:first-child');
    if (firstSlide) {
      firstSlide.setAttribute('aria-hidden', 'false');
      firstSlide.removeAttribute('hidden');
      firstSlide.style.contentVisibility = 'visible';
      // Garantir que a animação funcione
      firstSlide.style.opacity = '';
      firstSlide.style.visibility = '';
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
